/**
 * Resolves once the app's stylesheet has loaded, so React's first render is never
 * drawn without it.
 *
 * In the built site, scripts/prerender.mjs serves the stylesheet as a non-blocking
 * <link media="print" onload="this.media='all'" data-main-css>, so the first paint no
 * longer waits for it (the "render-blocking stylesheet" SEO audit finding). Until it
 * arrives, the page shows its prerendered copy, which the inline <style> in
 * public/index.html styles on its own. React replaces that copy with the real page,
 * so it must wait for the stylesheet first.
 *
 * Never rejects, and never waits forever:
 *   - a failed load resolves too, and the app renders, as it would have after a failed
 *     blocking stylesheet. A failure that happened before this script ran is read
 *     from the link (its inline onerror sets data-main-css="failed"), because that
 *     error event has already fired and cannot be listened for any more;
 *   - after MAIN_CSS_WAIT_MS it resolves whatever happened, in case neither event is
 *     ever seen (a browser that reports neither, or inline handlers blocked).
 * While the file is on its way it waits, much as the old blocking stylesheet held the
 * app's script back. With no such link (the dev server, tests) it resolves at once.
 */
export const MAIN_CSS_WAIT_MS = 10000;

const mainCssReady = (doc = document, waitMs = MAIN_CSS_WAIT_MS) => new Promise((resolve) => {
  const link = doc.querySelector('link[data-main-css]');
  if (!link) {
    resolve();
    return;
  }
  // The onload attribute does this too; doing it here as well means the styles apply
  // even when that handler has not run yet, or was blocked.
  const apply = () => {
    link.media = 'all';
    resolve();
  };
  // Already loaded before this script ran: the inline handler has switched it on, or
  // the sheet is there and only the switch is left.
  if (link.media === 'all' || link.sheet) {
    apply();
    return;
  }
  // Already failed before this script ran.
  if (link.getAttribute('data-main-css') === 'failed') {
    resolve();
    return;
  }
  link.addEventListener('load', apply, { once: true });
  link.addEventListener('error', () => resolve(), { once: true });
  setTimeout(resolve, waitMs);
});

export default mainCssReady;
